import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logoIcon from './logo.svg';
import aweGenerateImage from './physical-ai-awe-generate.png';
import architectsInterveneImage from './physical-ai-architects-intervene.png';
import trainingAnalysisVideo from './physical-ai-training-analysis.mp4';
import trainingPlanVideo from './physical-ai-training-plan.mp4';
import trainingViewVideo from './physical-ai-training-view.mp4';
import './PhysicalAIData.css';

const sections = [
  {
    number: '1',
    kicker: 'Architecture world engine',
    title: 'Architecture World Engine Generates the Scene',
    body:
      'The architecture world engine generates rich, vivid scenes while staying rule-compliant. It understands terrain, massing, circulation, program logic, and spatial constraints together, so each world can feel alive without breaking architectural rules.',
    placeholder: 'Placeholder engine input',
    image: aweGenerateImage,
    imageAlt: 'Architecture world engine generated scene preview',
    type: 'engine',
  },
  {
    number: '2',
    kicker: 'Architect intervention',
    title: 'Real Architects Make the Scene Alive',
    body:
      'With real architects involved, every scene gains human perspective, spatial judgment, personality, and vivid fidelity. Their design experience adds relationships that AI alone cannot fully invent. We already have contracts with East China Architectural Design & Research Institute and Tongji Architectural Design Group, giving us access to a potential reserve of roughly 3,000 to 5,000 architects.',
    placeholder: 'Placeholder architect diagram',
    image: architectsInterveneImage,
    imageAlt: 'Architect intervention diagram preview',
    type: 'architect',
  },
  {
    number: '3',
    kicker: 'Physical AI training',
    title: 'Potential Data for Physical AI Training',
    body:
      'Generated worlds and architect-polished scenes become structured motion, view, and interaction data for physical AI training. The sample below shows how spatial capture can become training material for agents that need to reason through built environments.',
    placeholder: 'Training data sample',
    videos: {
      analysis: trainingAnalysisVideo,
      plan: trainingPlanVideo,
      view: trainingViewVideo,
    },
    type: 'training',
  },
];

const EngineSource = () => (
  <div className="pad-source-card pad-engine-source" aria-label="Instant Urban engine placeholder">
    <img src={logoIcon} alt="" />
    <span>Instant Urban</span>
    <strong>World Engine</strong>
  </div>
);

const ArchitectSource = () => (
  <div className="pad-source-card pad-architect-source" aria-label="Architect input placeholder">
    <div className="pad-architect-icon">
      <span />
      <span />
      <span />
    </div>
    <span>Architects</span>
    <strong>Human Judgment</strong>
  </div>
);

const dataStreamPanes = [
  { className: 'pad-stream-pane-primary', label: '01', sourceKey: 'view' },
  { className: 'pad-stream-pane-analysis', label: '02', sourceKey: 'analysis' },
  { className: 'pad-stream-pane-plan', label: '03', sourceKey: 'plan' },
];

const DataStreamVideoWall = ({ section, onVideoReady }) => {
  const videoRefs = useRef([]);
  const readyPaneKeysRef = useRef(new Set());
  const hasStartedRef = useRef(false);

  const syncFollowers = useCallback(() => {
    const [primary, ...followers] = videoRefs.current.filter(Boolean);

    if (!primary || followers.length === 0) {
      return;
    }

    followers.forEach((video) => {
      if (Math.abs(video.currentTime - primary.currentTime) > 0.08) {
        video.currentTime = primary.currentTime;
      }

      if (primary.paused && !video.paused) {
        video.pause();
      }

      if (!primary.paused && video.paused) {
        video.play().catch(() => {});
      }

      if (video.playbackRate !== primary.playbackRate) {
        video.playbackRate = primary.playbackRate;
      }
    });
  }, []);

  const handlePaneReady = useCallback((sourceKey) => {
    readyPaneKeysRef.current.add(sourceKey);

    if (readyPaneKeysRef.current.size !== dataStreamPanes.length || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    const videos = videoRefs.current.filter(Boolean);

    videos.forEach((video) => {
      video.currentTime = 0;
      video.muted = true;
      video.playbackRate = 1;
    });

    Promise.allSettled(videos.map((video) => video.play()))
      .finally(() => {
        syncFollowers();
        onVideoReady();
      });
  }, [onVideoReady, syncFollowers]);

  useEffect(() => {
    const timer = window.setInterval(syncFollowers, 350);

    return () => {
      window.clearInterval(timer);
    };
  }, [syncFollowers]);

  return (
    <div className="pad-media-frame pad-video-frame pad-data-stream-wall">
      {dataStreamPanes.map((pane, index) => (
        <div className={`pad-stream-pane ${pane.className}`} key={pane.label}>
          <video
            className="pad-training-video"
            muted
            loop
            playsInline
            preload="auto"
            ref={(node) => {
              videoRefs.current[index] = node;
            }}
            onLoadedData={() => handlePaneReady(pane.sourceKey)}
            onCanPlay={() => handlePaneReady(pane.sourceKey)}
            onPlay={syncFollowers}
            onPause={syncFollowers}
            onSeeked={syncFollowers}
            onError={() => handlePaneReady(pane.sourceKey)}
            src={section.videos[pane.sourceKey]}
          />
          <span className="pad-stream-pane-number">{pane.label}</span>
          {pane.label === '01' && (
            <div className="pad-media-caption">{section.placeholder}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const MediaFrame = ({ section, onVideoReady }) => {
  if (section.videos) {
    return <DataStreamVideoWall section={section} onVideoReady={onVideoReady} />;
  }

  return (
    <div className="pad-media-frame">
      <img src={section.image} alt={section.imageAlt} />
      <div className="pad-media-caption">{section.placeholder}</div>
    </div>
  );
};

const PhysicalAIData = () => {
  const videoReadyRef = useRef(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    if (videoReadyRef.current) {
      setIsVideoReady(true);
    }
  }, []);

  const handleVideoReady = () => {
    videoReadyRef.current = true;
    setIsVideoReady(true);
  };

  return (
    <div className="physical-ai-data-page">
      <nav className="pad-navbar">
        <Link to="/" className="pad-brand">
          <img src={logoIcon} alt="Instant Urban logo" className="pad-logo" />
          <span className="pad-brand-text">Instant Urban</span>
        </Link>
        <Link to="/" className="pad-back-link">Back to home</Link>
      </nav>

      <main>
        <section className="pad-genie-hero">
          <div className="pad-hero-copy">
            <p className="pad-kicker">Physical AI Data</p>
            <h1>Architecture worlds for physical intelligence</h1>
            <p>
              A world-generation pipeline for architectural scenes, architect intervention,
              and training data that can teach embodied agents how built space behaves.
            </p>
          </div>
        </section>

        {sections.map((section, index) => (
          <section className="pad-genie-section" key={section.number}>
            <div className="pad-section-copy">
              <div className="pad-section-index">{section.number}</div>
              <p className="pad-kicker">{section.kicker}</p>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </div>

            <div className={`pad-flow-stage pad-flow-${section.type}`}>
              {section.type === 'engine' && <EngineSource />}
              {section.type === 'architect' && <ArchitectSource />}
              {section.type === 'training' && (
                <div className="pad-source-card pad-training-source">
                  <span>Generated views</span>
                  <strong>Data Stream</strong>
                </div>
              )}

              <div className="pad-flow-arrow" aria-hidden="true">
                <span />
                <i />
              </div>

              <MediaFrame section={section} onVideoReady={handleVideoReady} />

              {section.videos && !isVideoReady && (
                <div className="pad-video-loader" aria-live="polite">
                  <div className="pad-video-spinner" />
                  <span>Loading training sample...</span>
                </div>
              )}
            </div>

            {index < sections.length - 1 && (
              <div className="pad-section-divider" aria-hidden="true">
                <span />
              </div>
            )}
          </section>
        ))}
      </main>
    </div>
  );
};

export default PhysicalAIData;
